import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Referral() {
  // SEO Configuration
  useSEO({
    title: 'Submit a Referral - The Villarreal Health Network',
    description: 'Submit a healthcare referral to The Villarreal Health Network. Fast response within 15 minutes for home health, skilled nursing, personal care, and therapy services in Las Vegas.',
    keywords: 'healthcare referral, home health referral, patient referral form, skilled nursing referral, care coordination Las Vegas',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "name": "Submit Healthcare Referral",
          "description": "Submit a referral for home healthcare services",
          "provider": getOrganizationSchema()
        },
        getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Submit a Referral', url: '/referral' }
        ])
      ]
    }
  });

  const [formData, setFormData] = useState({
    referrerType: '',
    referrerName: '',
    referrerPhone: '',
    referrerEmail: '',
    relationship: '',
    patientName: '',
    patientAge: '',
    patientAddress: '',
    careTypes: [],
    urgency: 'routine',
    preferredAgency: '',
    insuranceType: '',
    additionalNotes: '',
    hasDocuments: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const careTypes = [
    'Skilled Nursing',
    'Primary Care',
    'Psychiatric Services',
    'Pain Management',
    'Wound Care',
    'Physical Therapy',
    'Occupational Therapy',
    'Personal Care (ADLs)',
    'Companion Care',
    'Alzheimer\'s/Dementia Support',
    'Respite Care',
    'Medication Management'
  ];

  const agencies = [
    'Med-Care Providers',
    'Celebrity Home Health',
    'Accent Senior Care',
    'A Peace of Mind Caregivers',
    'No Preference - Help Me Choose'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && name === 'careTypes') {
      setFormData(prev => ({
        ...prev,
        careTypes: checked 
          ? [...prev.careTypes, value]
          : prev.careTypes.filter(item => item !== value)
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/submit-referral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          referrerType: '',
          referrerName: '',
          referrerPhone: '',
          referrerEmail: '',
          relationship: '',
          patientName: '',
          patientAge: '',
          patientAddress: '',
          careTypes: [],
          urgency: 'routine',
          preferredAgency: '',
          insuranceType: '',
          additionalNotes: '',
          hasDocuments: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting referral:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-teal-600 to-blue-700 text-white py-24 pt-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-block bg-white/20 text-white px-8 py-4 rounded-full text-xl font-semibold backdrop-blur-sm shadow-lg">
                Start Your Healthcare Journey
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-8">
              Submit a Referral
            </h1>
            <p className="text-2xl text-blue-100 max-w-3xl mx-auto">
              Help us connect your patient or loved one with the right home healthcare services. We'll respond within 15 minutes during business hours.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitStatus === 'success' && (
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-10 mb-12">
              <div className="flex items-center">
                <i className="ri-check-circle-line text-green-600 text-4xl mr-6"></i>
                <div>
                  <h3 className="text-green-800 font-bold text-2xl">Referral Submitted Successfully!</h3>
                  <p className="text-green-700 text-lg">Our intake coordinator will contact us within 15 minutes during business hours.</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-10 mb-12">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-600 text-4xl mr-6"></i>
                <div>
                  <h3 className="text-red-800 font-bold text-2xl">Submission Error</h3>
                  <p className="text-red-700 text-lg">Please try again or call us directly at (702) 555-1234.</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-12 border border-gray-100">
            {/* Referrer Information */}
            <div className="mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-10">Referrer Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label className="block font-semibold text-gray-700 mb-4 text-xl">
                    I am a: *
                  </label>
                  <select
                    name="referrerType"
                    value={formData.referrerType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg pr-8"
                  >
                    <option value="">Select your role</option>
                    <option value="family">Family Member</option>
                    <option value="patient">Patient</option>
                    <option value="discharge-planner">Hospital Discharge Planner</option>
                    <option value="case-manager">Case Manager</option>
                    <option value="social-worker">Social Worker</option>
                    <option value="physician">Physician</option>
                    <option value="other-professional">Other Healthcare Professional</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 mb-4 text-xl">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="referrerName"
                    value={formData.referrerName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 mb-4 text-xl">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="referrerPhone"
                    value={formData.referrerPhone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    placeholder="(702) 555-0123"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 mb-4 text-xl">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="referrerEmail"
                    value={formData.referrerEmail}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    placeholder="your.email@example.com"
                  />
                </div>

                {formData.referrerType === 'family' && (
                  <div className="md:col-span-2">
                    <label className="block font-semibold text-gray-700 mb-4 text-xl">
                      Relationship to Patient
                    </label>
                    <input
                      type="text"
                      name="relationship"
                      value={formData.relationship}
                      onChange={handleInputChange}
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                      placeholder="e.g., Daughter, Son, Spouse"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Patient Information */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Patient Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block font-medium text-gray-700 mb-3 text-lg">
                    Patient Name *
                  </label>
                  <input
                    type="text"
                    name="patientName"
                    value={formData.patientName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    placeholder="Patient's full name"
                  />
                </div>

                <div>
                  <label className="block font-medium text-gray-700 mb-3 text-lg">
                    Patient Age
                  </label>
                  <input
                    type="number"
                    name="patientAge"
                    value={formData.patientAge}
                    onChange={handleInputChange}
                    min="0"
                    max="120"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    placeholder="Age"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block font-medium text-gray-700 mb-3 text-lg">
                    Patient Address *
                  </label>
                  <textarea
                    name="patientAddress"
                    value={formData.patientAddress}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    placeholder="Street address, city, state, zip code"
                  />
                </div>
              </div>
            </div>

            {/* Care Needs */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Care Needs</h2>
              
              <div className="mb-8">
                <label className="block font-medium text-gray-700 mb-4 text-lg">
                  Type of Care Needed (select all that apply) *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {careTypes.map((careType) => (
                    <label key={careType} className="flex items-center cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <input
                        type="checkbox"
                        name="careTypes"
                        value={careType}
                        checked={formData.careTypes.includes(careType)}
                        onChange={handleInputChange}
                        className="mr-3 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-gray-700">{careType}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block font-medium text-gray-700 mb-3 text-lg">
                    Urgency Level *
                  </label>
                  <select
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg pr-8"
                  >
                    <option value="routine">Routine (within 48 hours)</option>
                    <option value="urgent">Urgent (within 24 hours)</option>
                    <option value="stat">STAT (immediate)</option>
                  </select>
                </div>

                <div>
                  <label className="block font-medium text-gray-700 mb-3 text-lg">
                    Preferred Agency
                  </label>
                  <select
                    name="preferredAgency"
                    value={formData.preferredAgency}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg pr-8"
                  >
                    <option value="">Select preferred agency</option>
                    {agencies.map((agency) => (
                      <option key={agency} value={agency}>{agency}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-medium text-gray-700 mb-3 text-lg">
                    Insurance Type
                  </label>
                  <select
                    name="insuranceType"
                    value={formData.insuranceType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg pr-8"
                  >
                    <option value="">Select insurance type</option>
                    <option value="medicare">Medicare</option>
                    <option value="medicaid">Medicaid</option>
                    <option value="medicaid-waiver">Medicaid Waiver</option>
                    <option value="private-insurance">Private Insurance</option>
                    <option value="private-pay">Private Pay</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Additional Information</h2>
              
              <div className="mb-8">
                <label className="block font-medium text-gray-700 mb-3 text-lg">
                  Additional Notes or Medical History
                </label>
                <textarea
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleInputChange}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  placeholder="Please include any relevant medical conditions, current medications, mobility limitations, or special care requirements..."
                />
                <p className="text-sm text-gray-500 mt-2">
                  {formData.additionalNotes.length}/500 characters
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="hasDocuments"
                  checked={formData.hasDocuments}
                  onChange={handleInputChange}
                  className="mr-4 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-gray-700 text-lg">
                  I have documents to upload (discharge summary, physician orders, insurance cards)
                </label>
              </div>
              
              {formData.hasDocuments && (
                <div className="mt-6 p-6 bg-blue-50 rounded-lg">
                  <p className="text-blue-800">
                    <i className="ri-information-line mr-2"></i>
                    Our intake coordinator will contact you to securely collect any necessary documents.
                  </p>
                </div>
              )}
            </div>

            {/* HIPAA Notice */}
            <div className="mb-12 p-8 bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl border border-gray-200">
              <h3 className="font-bold text-gray-900 mb-4 text-xl">
                <i className="ri-shield-check-line mr-3 text-blue-600"></i>
                HIPAA Privacy Notice
              </h3>
              <p className="text-gray-700 text-lg">
                By submitting this form, you acknowledge that you have the authority to share this patient information and consent to our use of this information to coordinate appropriate home healthcare services. All information is handled in compliance with HIPAA privacy regulations.
              </p>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting || formData.careTypes.length === 0}
                className="w-full md:w-auto whitespace-nowrap text-xl px-12 py-5 shadow-xl"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line mr-3 animate-spin"></i>
                    Submitting Referral...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-3"></i>
                    Submit Referral
                  </>
                )}
              </Button>
              
              <p className="text-gray-600 mt-8 text-lg">
                <i className="ri-time-line mr-2"></i>
                We'll respond within 15 minutes during business hours (Mon-Fri 8AM-6PM)
              </p>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
